// Components==============
import storeWrongAnswer from 'actions/wrongAnswer/store';
import DragNDropTile from 'components/UI/DragNDropTile/DragNDropTile';
import HiddenInput from 'components/UI/HiddenInput/HiddenInput';
import { SoundType } from 'components/UI/LetterBlock/LetterBlock';
import StatusBox, { BoxTypes } from 'components/UI/StatusBox/StatusBox';
import { motion } from 'framer-motion';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { HomeworkContext } from 'screens/Homework/Homework';
import { compareArray } from 'utilities/compareArray';
import { convertArray, deConvertArray } from 'utilities/convertArray';
import { SoundsExerciseContext } from '../../SoundsExercise';
import Text from '../Text/Text';
import { useBlockSize } from '../useBlockSize';
import './Row.module.scss';
// =========================

export const defaultTileBarStateValue = Array(10).fill('');

export default function Row({
    id,
    setAmountOfRows,
    wordsInCategory,
    setTriggerAnimation,
    name,
    withImage,
    category_id,
}: {
    id: string;
    setAmountOfRows?: React.Dispatch<React.SetStateAction<number>>;
    wordsInCategory?: string[];
    setTriggerAnimation?: React.Dispatch<React.SetStateAction<boolean>>;
    name?: string;
    withImage?: boolean;
    category_id?: number;
}) {
    const tiles = Array.from(Array(10).keys());

    const { sequence } = useContext(HomeworkContext);

    const {
        dragState,
        activeTileBar,
        setActiveTileBar,
        setActiveTileBarState,
        activeTileBarState,
        setUserIsTypingAnswer,
        toggleIsUpdatedOnClick,
        answer,
        userAnswers,
        setCurrent,
        setUserAnswers,
        current,
        categories,
        words,
        useCategories,
        useInput,
        useTiles,
        givenAnswers,
    } = useContext(SoundsExerciseContext);

    const [tileBarState, setTileBarState] = useState<SoundType[]>(
        defaultTileBarStateValue
    );
    const [tileBarStateAnswer, setTileBarStateAnswer] = useState<SoundType[]>(
        defaultTileBarStateValue
    );

    const [text, setText] = useState('');
    const [displayText, setDisplayText] = useState(false);
    const [displayInput, setDisplayInput] = useState(false);

    const item = dragState?.draggableId!;
    const source = dragState?.source.droppableId;
    const destination = dragState?.destination?.droppableId;
    const destinationIndex = parseInt(destination?.charAt(4) || '-1');
    const sourceIndex = parseInt(source?.charAt(4) || '-1');
    const [boxState, setBoxState] = useState<BoxTypes>('initial');
    const [attempts, setAttempts] = useState(0);

    const tilePartIsFinished = boxState === 'correct' || boxState === 'failed';

    const dispatch = useDispatch();
    const { treatmentId, homeworkId } = useParams() as {
        treatmentId: string;
        homeworkId: string;
    };

    const index = parseInt(id[id.length - 1]);

    const alreadyAnsweredSingle = givenAnswers?.find(
        (a) => a.correctText === name
    );
    const alreadyAnsweredCat = givenAnswers?.filter(
        (a) =>
            wordsInCategory?.includes(a.correctText) &&
            (a.category_id ? a.category_id === category_id : true)
    )?.[index - 1];

    const alreadyAnswered = useCategories
        ? alreadyAnsweredCat
        : alreadyAnsweredSingle;

    // set values if answer is already given
    useEffect(() => {
        if (useTiles) {
            if (alreadyAnswered?.correctBars) {
                setTileBarState(
                    alreadyAnswered.correctBars.map((bar) =>
                        bar === null ? '' : bar
                    )
                );
                if (useInput) setDisplayInput(true);
                if (!useInput) setDisplayText(true);
                setText(alreadyAnswered.correctText);
                if (
                    compareArray(
                        alreadyAnswered.bars,
                        alreadyAnswered.correctBars
                    )
                )
                    return setBoxState('correct');

                setBoxState('failed');
                return;
            }
            setTileBarState(defaultTileBarStateValue);
            if (useInput) setDisplayInput(false);
            if (!useInput) setDisplayText(false);
            setText('');
            setBoxState('initial');
        }
    }, [alreadyAnswered]);

    useEffect(() => {
        if (wordsInCategory && setTriggerAnimation) {
            // Trigger wrong column animation
            if (
                !wordsInCategory.includes(answer?.name) &&
                destination?.includes(id)
            ) {
                setTriggerAnimation(true);
            }

            // Check if is the right column
            if (!wordsInCategory.includes(answer?.name)) return;
        }

        if (destinationIndex > -1 && destination?.includes(id)) {
            // Add item
            if (source === 'bar') {
                return setTileBarState((prev) =>
                    prev.map((e, i) => {
                        if (i === destinationIndex) return item as SoundType;
                        return e;
                    })
                );
            }

            // Replace item
            return setTileBarState((prev) =>
                prev.map((e, i) => {
                    if (i === destinationIndex && i === sourceIndex) return e;
                    if (i === destinationIndex)
                        return tileBarState[sourceIndex];
                    if (i === sourceIndex) return '';
                    return e;
                })
            );
        }

        // COMES WITH THE FOLLOWING BUG -> DRAG DELETE -> CLICK ADD ITEM -> BAR CLEARS
        // // Delete item
        // if (!destination && source !== 'bar') {
        //     return setTileBarState((prev) =>
        //         prev.map((e, i) => {
        //             if (i === sourceIndex) return '';
        //             return e;
        //         })
        //     );
        // }
    }, [dragState]);

    useEffect(() => {
        if (alreadyAnswered) return;
        if (boxState === 'failed') return;
        const resetTileBarState = () => {
            setActiveTileBarState(defaultTileBarStateValue);
            setBoxState('initial');
            if (useCategories) {
                setActiveTileBar('initial');
            }
        };

        if (tilePartIsFinished && !useInput) return resetTileBarState();

        if (
            typeof tileBarState.find((e) => e !== '') !== 'undefined' &&
            !text
        ) {
            setBoxState('submit');
            setActiveTileBarState(tileBarState);
            return setActiveTileBar(id);
        }
        resetTileBarState();
    }, [tileBarState]);

    useEffect(() => {
        if (!useCategories && activeTileBar === id)
            return setTileBarState(activeTileBarState);

        if (
            typeof tileBarState.find((e) => e !== '') !== 'undefined' &&
            activeTileBar === id
        ) {
            setTileBarState(activeTileBarState);
        }
    }, [toggleIsUpdatedOnClick]);

    const mutatedArr = convertArray(activeTileBarState)?.filter(
        (tile) => tile !== ''
    );

    function storeWrong() {
        dispatch(
            storeWrongAnswer({
                given_answer: JSON.stringify(mutatedArr),
                correct_answer: JSON.stringify({
                    klanken: answer?.klanken,
                    woord: answer?.name,
                }),
                metadata: {
                    sequence,
                },
                type: 'klanken',
                exercise_id: homeworkId,
                exercise_type: 'homework',
                treatment_id: treatmentId,
            })
        );
    }

    function tryAgain() {
        storeWrong();
        setBoxState('wrong');
        setTimeout(() => {
            setBoxState('submit');
        }, 1500);
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (
            boxState === 'initial' ||
            boxState === 'failed' ||
            boxState === 'wrong'
        )
            return;

        if (activeTileBar === id) {
            if (compareArray(mutatedArr, answer?.klanken) || attempts === 2) {
                if (
                    attempts === 2 &&
                    !compareArray(mutatedArr, answer?.klanken)
                ) {
                    setTileBarState(deConvertArray(answer?.klanken));
                    storeWrong();
                    setBoxState('failed');
                } else {
                    setBoxState('correct');
                }

                if (useInput && useTiles) {
                    setTileBarStateAnswer(tileBarState);
                }

                if (useInput) {
                    setDisplayInput(true);
                    setUserIsTypingAnswer(true);
                } else {
                    setText(answer?.name);
                    setUserAnswers((prev) => [
                        ...prev,
                        {
                            bars: tileBarState,
                            text: answer.name,
                            correctText: answer.name,
                            correctBars: deConvertArray(answer?.klanken),
                            id: answer.id,
                            category_id,
                        },
                    ]);

                    if (useCategories && current < words.length - 1) {
                        // timeout is to make sure that the current equals the userAnswers array in SoundsExercise.tsx
                        setTimeout(() => {
                            setCurrent((prev) => prev + 1);
                        }, 100);
                        if (setAmountOfRows)
                            setAmountOfRows((prev) => prev + 1);
                    }

                    setDisplayText(true);
                }
            } else {
                setAttempts((prev) => prev + 1);
                if (attempts < 3) tryAgain();
            }
        }
    }

    const answerIsFilledIn = userAnswers[current] !== undefined;

    const condition =
        !tilePartIsFinished &&
        !answerIsFilledIn &&
        (activeTileBar === 'initial' || activeTileBar === id);

    const droppableCondition = !useCategories
        ? answer?.name === name && condition
        : condition;

    useEffect(() => {
        if (!useCategories) {
            if (name === answer?.name) {
                if (tilePartIsFinished) return setActiveTileBar('initial');

                if (typeof tileBarState.find((e) => e !== '') === 'undefined') {
                    setActiveTileBarState(defaultTileBarStateValue);
                }
                setActiveTileBar(id);
                setAttempts(0);
            }
        }
    }, [answer, tilePartIsFinished]);

    const blockSize = useBlockSize(!useCategories ? 1 : categories.length);

    const size = withImage ? '20px' : blockSize;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            styleName="grid"
        >
            {useInput && !useTiles ? (
                <Text
                    setAmountOfRows={setAmountOfRows}
                    name={name}
                    setTriggerAnimation={setTriggerAnimation}
                    alreadyAnswered={alreadyAnswered}
                    category_id={category_id}
                />
            ) : (
                <>
                    <div
                        styleName={`wrapper ${
                            parseFloat(size) > 30 ? 'large-icon' : ''
                        }`}
                        style={{
                            gridTemplateColumns: `repeat(11, ${size})`,
                            gridTemplateRows: size,
                        }}
                    >
                        {tiles.map((tile, i) => {
                            const idProp = `bar-${tile}-${id}`;

                            return (
                                <DragNDropTile
                                    key={i}
                                    index={i}
                                    id={idProp}
                                    droppableCondition={droppableCondition}
                                    setTileBarState={setTileBarState}
                                    tilePartIsFinished={tilePartIsFinished}
                                    tileBarState={tileBarState}
                                />
                            );
                        })}
                        <form onSubmit={handleSubmit}>
                            <StatusBox boxState={boxState} fullSize />
                            <HiddenInput effect={tileBarState} />
                        </form>
                    </div>
                    {displayText && (
                        <Text text={text} category_id={category_id} />
                    )}
                    {displayInput && (
                        <Text
                            setAmountOfRows={setAmountOfRows}
                            tileBarStateAnswer={tileBarStateAnswer}
                            name={name}
                            alreadyAnswered={alreadyAnswered}
                            category_id={category_id}
                        />
                    )}
                </>
            )}
        </motion.div>
    );
}
